'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket, joinUserRoom, leaveUserRoom, EVENTS } from '../lib/socket';
import { useSession } from './useSession';
import type {
  TransactionUpdateEvent,
  PaymentEvent,
  DeviceApprovalRequestEvent,
  DeviceResponseEvent,
  BalanceUpdateEvent,
  KycStatusUpdateEvent,
  SocketEventMap,
  SocketEventName,
} from '../lib/socket';
import { useBalances } from './useBalances';
import { useUsdcBalance } from './useUsdcBalance';

type EventHandler<T = any> = (data: T) => void;

interface SocketHookReturn {
  isConnected: boolean;
  isListening: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected';
  addEventListener: <K extends SocketEventName>(
    event: K,
    handler: EventHandler<SocketEventMap[K]>
  ) => () => void;
  removeEventListener: <K extends SocketEventName>(
    event: K,
    handler: EventHandler<SocketEventMap[K]>
  ) => void;
  emit: (event: string, data?: any) => Promise<void>;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
}

// Global state (HMR-safe) to prevent multiple initializations and support ref-counting
declare global {
  // eslint-disable-next-line no-var
  var __wafraSocketState__: {
    isListening: boolean;
    handlersRegistered: boolean;
    refCount: number;
  } | undefined;
}

const getGlobalSocketState = () => {
  if (!globalThis.__wafraSocketState__) {
    globalThis.__wafraSocketState__ = {
      isListening: false,
      handlersRegistered: false,
      refCount: 0,
    };
  }
  return globalThis.__wafraSocketState__;
};

export function useSocket(): SocketHookReturn {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const { refetchBalance } = useBalances();
  const { refetch: refetchUsdcBalance } = useUsdcBalance();

  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected'
  >('disconnected');

  const eventHandlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const socketRef = useRef<Awaited<ReturnType<typeof getSocket>> | null>(null);

  const stopListening = useCallback(async () => {
    const globalState = getGlobalSocketState();
    if (globalState.refCount > 0) {
      globalState.refCount -= 1;
    }

    // Only perform teardown when no more listeners remain
    if (globalState.refCount > 0) {
      return;
    }

    if (!globalState.isListening || !socketRef.current) {
      console.log('Socket not listening');
      return;
    }

    try {
      const socket = socketRef.current;

      // Remove all event listeners
      eventHandlersRef.current.forEach((handlers, event) => {
        handlers.forEach((handler) => {
          socket.off(event, handler);
        });
      });

      // Leave user room
      if (session?.user?.id) {
        await leaveUserRoom(session.user.id);
      }

      // Clear event handlers
      eventHandlersRef.current.clear();

      setIsListening(false);
      setIsConnected(false);
      setConnectionStatus('disconnected');
      socketRef.current = null;
      globalState.isListening = false;
      globalState.handlersRegistered = false;

      console.log('🔌 Socket listening stopped');
    } catch (error) {
      console.error('Error stopping socket listening:', error);
    }
  }, [session?.user?.id]);

  const removeEventListener = useCallback(
    <K extends SocketEventName>(event: K, handler: EventHandler<SocketEventMap[K]>) => {
      if (!socketRef.current) {
        return;
      }

      const socket = socketRef.current;

      // Remove from our tracking
      const handlers = eventHandlersRef.current.get(event);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          eventHandlersRef.current.delete(event);
        }
      }

      // Remove from socket
      socket.off(event as any, handler as any);

      console.log(`📡 Removed listener for event: ${event}`);
    },
    []
  );

  const addEventListener = useCallback(
    <K extends SocketEventName>(event: K, handler: EventHandler<SocketEventMap[K]>) => {
      if (!socketRef.current) {
        console.warn('Socket not initialized. Call startListening first.');
        return () => {};
      }

      const socket = socketRef.current;

      // Add handler to our tracking
      if (!eventHandlersRef.current.has(event)) {
        eventHandlersRef.current.set(event, new Set());
      }
      eventHandlersRef.current.get(event)!.add(handler);

      // Add to socket
      socket.on(event as any, handler as any);

      console.log(`📡 Added listener for event: ${event} on socket ${socket.id}`);

      // Return cleanup function
      return () => {
        removeEventListener(event, handler);
      };
    },
    [removeEventListener]
  );

  const emit = useCallback(async (event: string, data?: any) => {
    if (!socketRef.current) {
      console.warn('Socket not initialized. Call startListening first.');
      return;
    }

    const socket = socketRef.current;
    console.log(`📡 Emitting event: ${event} on socket ${socket.id}`, data);
    console.log(`📡 Socket connected: ${socket.connected}`);

    socket.emit(event, data);
    console.log(`📡 Event emitted successfully: ${event}`);
  }, []);

  const startListening = useCallback(async () => {
    if (!session?.user?.id) {
      console.warn('Cannot start socket listening: User not authenticated');
      return;
    }

    const globalState = getGlobalSocketState();
    globalState.refCount += 1;

    try {
      setConnectionStatus('connecting');
      const socket = await getSocket();
      socketRef.current = socket;
      globalState.isListening = true;

      // Join user room for personalized updates
      if (socket && socket.connected) {
        await joinUserRoom(session.user.id);
      } else {
        // Join after connect
        socket?.once('connect', () => joinUserRoom(session.user!.id));
      }

      // Connection event handlers
      const handleConnect = () => {
        console.log('🔌 Socket connected:', socket?.id);
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsListening(true);
      };

      const handleDisconnect = () => {
        console.log('🔌 Socket disconnected:', socket?.id);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setIsListening(false);
        globalState.isListening = false;
      };

      // Default event handlers for common events
      const handleTransactionUpdate = (data: TransactionUpdateEvent) => {
        console.log('📡 Transaction update received:', data);
        if (data.type === 'deposit' && data.status === 'completed') {
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
          queryClient.invalidateQueries({ queryKey: ['balances'] });
          queryClient.invalidateQueries({ queryKey: ['history'] });
          refetchBalance();
          refetchUsdcBalance();
        }
      };

      const handlePaymentEvent = (data: PaymentEvent) => {
        console.log('💳 Payment event received:', data);
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['balances'] });
      };

      const handleBalanceUpdate = (data: BalanceUpdateEvent) => {
        console.log('💰 Balance update received:', data);
        queryClient.invalidateQueries({ queryKey: ['balances'] });
        refetchBalance();
      };

      const handleKycStatusUpdate = (data: KycStatusUpdateEvent) => {
        console.log('📋 KYC status update received:', data);
        queryClient.invalidateQueries({ queryKey: ['kyc'] });
        queryClient.invalidateQueries({ queryKey: ['history'] });
      };

      const handleDeviceApprovalRequest = (data: DeviceApprovalRequestEvent) => {
        console.log('📱 Device approval request received:', data);
        queryClient.invalidateQueries({ queryKey: ['devices'] });
      };

      const handleDeviceResponse = (data: DeviceResponseEvent) => {
        console.log('📱 Device response received:', data);
        queryClient.invalidateQueries({ queryKey: ['devices'] });
      };

      // Register default handlers only once globally
      if (!globalState.handlersRegistered && socket) {
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on(EVENTS.TRANSACTION_UPDATE, handleTransactionUpdate);
        socket.on(EVENTS.PAYMENT_SUCCESS, handlePaymentEvent);
        socket.on(EVENTS.PAYMENT_FAILED, handlePaymentEvent);
        socket.on(EVENTS.DEPOSIT_SUCCESS, handlePaymentEvent);
        socket.on(EVENTS.DEPOSIT_FAILED, handlePaymentEvent);
        socket.on(EVENTS.BALANCE_UPDATE, handleBalanceUpdate);
        socket.on(EVENTS.KYC_STATUS_UPDATE, handleKycStatusUpdate);
        socket.on(EVENTS.DEVICE_APPROVAL_REQUEST, handleDeviceApprovalRequest);
        socket.on(EVENTS.DEVICE_APPROVED, handleDeviceResponse);
        socket.on(EVENTS.DEVICE_REJECTED, handleDeviceResponse);

        globalState.handlersRegistered = true;
        console.log('🔌 Global event handlers registered once');
      }

      // Check if already connected
      if (socket?.connected) {
        handleConnect();
      }

      // Store default handlers for cleanup
      eventHandlersRef.current.set('connect', new Set([handleConnect]));
      eventHandlersRef.current.set('disconnect', new Set([handleDisconnect]));
      eventHandlersRef.current.set(EVENTS.TRANSACTION_UPDATE, new Set([handleTransactionUpdate]));
      eventHandlersRef.current.set(EVENTS.PAYMENT_SUCCESS, new Set([handlePaymentEvent]));
      eventHandlersRef.current.set(EVENTS.PAYMENT_FAILED, new Set([handlePaymentEvent]));
      eventHandlersRef.current.set(EVENTS.DEPOSIT_SUCCESS, new Set([handlePaymentEvent]));
      eventHandlersRef.current.set(EVENTS.DEPOSIT_FAILED, new Set([handlePaymentEvent]));
      eventHandlersRef.current.set(EVENTS.BALANCE_UPDATE, new Set([handleBalanceUpdate]));
      eventHandlersRef.current.set(EVENTS.KYC_STATUS_UPDATE, new Set([handleKycStatusUpdate]));
      eventHandlersRef.current.set(
        EVENTS.DEVICE_APPROVAL_REQUEST,
        new Set([handleDeviceApprovalRequest])
      );
      eventHandlersRef.current.set(EVENTS.DEVICE_APPROVED, new Set([handleDeviceResponse]));
      eventHandlersRef.current.set(EVENTS.DEVICE_REJECTED, new Set([handleDeviceResponse]));
    } catch (error) {
      console.error('Error starting socket listening:', error);
      setConnectionStatus('disconnected');
      setIsListening(false);
    }
  }, [session?.user, queryClient, refetchBalance, refetchUsdcBalance]);

  // Auto-start listening when user is authenticated
  useEffect(() => {
    if (session?.user?.id && !isListening) {
      startListening();
    }
  }, [session?.user?.id, isListening, startListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isListening) {
        stopListening();
      }
    };
  }, [isListening, stopListening]);

  return {
    isConnected,
    isListening,
    connectionStatus,
    addEventListener,
    removeEventListener,
    emit,
    startListening,
    stopListening,
  };
}
